import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useMarkOptionsReviewedMutation, useOptionsQuery } from '../api';

import { useToast } from '@/components/ui/toast/ToastProvider';

/**
 * Variant-improvements lot — the "Vérifiez vos options" banner the
 * migration-strategy plan (résumée, Option 3) requires: shown only for a
 * product the backfill script touched (or that otherwise carries an
 * `auto_generated` option), never blocking — variants stay fully sellable
 * whether or not the merchant ever dismisses it, exactly like
 * `products.variant_options_reviewed_at`'s own header states.
 */
export function VariantOptionsReviewBanner({
  productId,
  variantOptionsReviewedAt,
}: {
  productId: string;
  variantOptionsReviewedAt: string | null;
}): ReactElement | null {
  const { t } = useI18n();
  const toast = useToast();
  const optionsQuery = useOptionsQuery(productId);
  const markReviewed = useMarkOptionsReviewedMutation(productId);

  const hasAutoGeneratedOption = (optionsQuery.data ?? []).some((option) => option.autoGenerated);
  if (variantOptionsReviewedAt !== null || !hasAutoGeneratedOption) return null;

  async function onDismiss(): Promise<void> {
    try {
      await markReviewed.mutateAsync();
      toast.success(t('merchant.products.reviewBanner.dismissedToast'));
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  return (
    <div
      role="status"
      className="flex flex-col gap-3 rounded-md border border-warn bg-warnSoft p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <p className="font-display text-sm font-semibold text-warn">
          {t('merchant.products.reviewBanner.title')}
        </p>
        <p className="text-sm text-ink">{t('merchant.products.reviewBanner.description')}</p>
      </div>
      <Button
        variant="secondary"
        loading={markReviewed.isPending}
        onClick={() => {
          void onDismiss();
        }}
      >
        {t('merchant.products.reviewBanner.dismiss')}
      </Button>
    </div>
  );
}
